/**
 * Get the browser's preferred languages in order of preference.
 */
function getBrowserLanguages(): readonly string[] {
    if (typeof navigator === "undefined") return [];

    if (navigator.languages?.length) {
        return navigator.languages;
    }

    if (navigator.language) {
        return [navigator.language];
    }

    return [];
}

/**
 * Select the best matching language from {@link validLanguages} based on the browser's
 * language preferences.
 *
 * For each browser language (in preference order), tries:
 * 1. Exact match (case-insensitive)
 * 2. Progressive sub-tag removal (CLDR inheritance), e.g. `zh-Hant-TW` → `zh-Hant` → `zh`
 *
 * @returns `undefined` if no match is found.
 */
export function autoSelectLanguage(validLanguages: string[]): string | undefined {
    // Pre-compute a lowercase -> original map for case-insensitive matching
    const validByLower = new Map<string, string>();
    for (const lang of validLanguages) {
        validByLower.set(lang.toLowerCase(), lang);
    }

    for (const browserLang of getBrowserLanguages()) {
        let candidate = browserLang.toLowerCase();

        // Try exact match
        const exact = validByLower.get(candidate);
        if (exact) return exact;

        // Progressively strip sub-tags (eg. `fr-CA` → `fr`, `zh-Hant-TW` → `zh-Hant` → `zh`)
        while (candidate.includes("-")) {
            candidate = candidate.substring(0, candidate.lastIndexOf("-"));
            const match = validByLower.get(candidate);
            if (match) return match;
        }
    }

    return undefined;
}

// cSpell:words CLDR
